import { useMemo } from 'react';
import useHouseholdOpenCertsQuery from './useHouseholdOpenCertsQuery';
import { buildApplicantHouseholdMembers } from './useMiniProfileSubject.helpers';

/**
 * The Mini-Profile's data-layer boundary between the two contexts it can be
 * opened from:
 *   { kind: 'resident',  residentId, householdId }
 *   { kind: 'applicant', applicationId, householdId }
 *
 * Deliberately does very little for the resident kind: MiniProfileBridge
 * keeps calling useHouseholdMembersQuery/useHouseholdPendingMembersQuery/
 * useResidentQuery/useHouseholdOpenCertsQuery directly with `residentId`,
 * exactly as it did before this hook existed, because ENG-30354's own bridge
 * tests assert on those calls directly (mocking each hook and checking its
 * args) -- routing the resident path through this hook too would mean
 * either rewriting those ~795 lines of tests or making them mock a hook
 * they no longer call, and the risk of quietly changing resident behavior
 * while doing so is exactly what this card's SPEC calls out as the biggest
 * risk. So this hook only owns the genuinely new applicant-shaped reads;
 * `subject.kind === 'resident'` (or no subject at all) makes it a no-op.
 *
 * `applicants` is `application.applicants` (Redux, not React Query) --
 * ApplicationProfile already loads it, so there's no second network fetch
 * here, only a reshape (useMiniProfileSubject.helpers.js) into the same
 * shape useHouseholdMembersQuery/useHouseholdPendingMembersQuery already
 * produce for a resident's household.
 */
const useMiniProfileSubject = ({
  subject,
  organizationId,
  propertyId,
  applicants,
  options = {},
}: {
  subject?: {
    kind: 'resident' | 'applicant',
    residentId?: string,
    applicationId?: string,
    householdId?: string,
  },
  organizationId: string,
  propertyId: string,
  applicants?: Array<Object>,
  options?: { enabled?: boolean },
}) => {
  const isApplicant = subject?.kind === 'applicant';
  const householdId = subject?.householdId ?? null;

  const members = useMemo(() => {
    if (!isApplicant) return [];
    return buildApplicantHouseholdMembers(applicants ?? []);
  }, [isApplicant, applicants]);

  // Household-scoped: a Pending household has no Resident row to key the
  // resident-scoped read off of. Gated on isApplicant so this never fires
  // for a resident-kind subject (or no subject at all) -- the resident path
  // keeps using its own useHouseholdOpenCertsQuery({ residentId }) call
  // inside MiniProfileBridge directly.
  const householdOpenCertsQuery = useHouseholdOpenCertsQuery({
    organizationId,
    propertyId,
    householdId,
    options: { enabled: isApplicant && options.enabled !== false },
  });

  return {
    members,
    householdId,
    householdHasOpenCerts: Boolean(householdOpenCertsQuery.data),
    isLoading: isApplicant && householdOpenCertsQuery.isLoading,
  };
};

export default useMiniProfileSubject;
